import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import {
  FileText, Clock, User, Activity, LogIn, DollarSign, Tag, UserCheck,
  RefreshCw, Check, X, Edit2, Trash2, Mail, Gift, Shield, TrendingUp,
  Users, Bell, ArrowUpCircle, ArrowDownCircle, Eye, Search, UserX
} from 'lucide-react';

const AdminAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('actions');
  const [actionFilter, setActionFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [adminList, setAdminList] = useState([]);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadData();
    loadAdminList();
  }, [actionFilter, adminFilter, days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (actionFilter) params.action_type = actionFilter;
      if (adminFilter) params.admin_id = adminFilter;
      
      const [logsRes, loginsRes] = await Promise.all([
        adminAPI.getAuditLog(params),
        adminAPI.getLoginHistory({ days })
      ]);
      
      setLogs(logsRes.data.logs || []);
      setLoginHistory(loginsRes.data.logins || []);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminList = async () => {
    try {
      const res = await adminAPI.getTeam();
      const team = res.data || [];
      // Filter to get only admins and managers
      setAdminList(team.filter(m => ['admin', 'manager', 'super_admin', 'support'].includes(m.role)));
    } catch (err) {
      console.error('Failed to load admin list:', err);
    }
  };

  const actionConfig = {
    // Mass actions
    mass_notification: { label: 'Массовое уведомление', color: '#3b82f6', icon: Bell },
    mass_status_change: { label: 'Массовое изменение статуса', color: '#8b5cf6', icon: RefreshCw },
    mass_assign: { label: 'Массовое назначение', color: '#06b6d4', icon: Users },
    mass_email: { label: 'Массовая рассылка', color: '#ec4899', icon: Mail },
    mass_tag: { label: 'Массовое добавление тега', color: '#14b8a6', icon: Tag },
    
    // Client actions
    client_create: { label: 'Создание клиента', color: COLORS.success, icon: UserCheck },
    client_update: { label: 'Обновление клиента', color: '#f59e0b', icon: Edit2 },
    client_delete: { label: 'Удаление клиента', color: COLORS.danger, icon: UserX },
    status_change: { label: 'Изменение статуса', color: '#8b5cf6', icon: RefreshCw },
    assign_manager: { label: 'Назначение менеджера', color: '#06b6d4', icon: UserCheck },
    balance_change: { label: 'Изменение баланса', color: COLORS.primary, icon: DollarSign },
    
    // Tags
    tag_add: { label: 'Добавление тега', color: '#14b8a6', icon: Tag },
    tag_remove: { label: 'Удаление тега', color: '#6b7280', icon: Tag },
    
    // Deposits & Withdrawals
    deposit_approve: { label: 'Одобрение депозита', color: COLORS.success, icon: ArrowUpCircle },
    deposit_reject: { label: 'Отклонение депозита', color: COLORS.danger, icon: ArrowUpCircle },
    deposit_create: { label: 'Создание депозита', color: '#3b82f6', icon: ArrowUpCircle },
    withdrawal_approve: { label: 'Одобрение вывода', color: COLORS.success, icon: ArrowDownCircle },
    withdrawal_complete: { label: 'Завершение вывода', color: COLORS.success, icon: ArrowDownCircle },
    withdrawal_reject: { label: 'Отклонение вывода', color: COLORS.danger, icon: ArrowDownCircle },
    
    // Positions
    position_create: { label: 'Создание позиции', color: COLORS.success, icon: TrendingUp },
    position_update: { label: 'Изменение позиции', color: '#3b82f6', icon: TrendingUp },
    position_close: { label: 'Закрытие позиции', color: COLORS.danger, icon: TrendingUp },
    
    // Auth
    admin_login: { label: 'Вход в панель', color: '#3b82f6', icon: LogIn },
    
    // Team
    team_create: { label: 'Создание сотрудника', color: COLORS.success, icon: Users },
    team_update: { label: 'Изменение сотрудника', color: '#f59e0b', icon: Users },
    team_delete: { label: 'Удаление сотрудника', color: COLORS.danger, icon: UserX },
    permissions_update: { label: 'Изменение прав', color: '#ef4444', icon: Shield },
    
    // IDO
    ido_transaction: { label: 'IDO транзакция', color: '#f0b90b', icon: Activity },
    ido_purchase: { label: 'Покупка IDO', color: COLORS.success, icon: Activity },
    ido_sell: { label: 'Продажа IDO', color: COLORS.danger, icon: Activity },
    
    // Email
    email_sent: { label: 'Отправка email', color: '#ec4899', icon: Mail },
    
    // Notes
    note_add: { label: 'Добавление заметки', color: '#8b5cf6', icon: FileText },
    note_delete: { label: 'Удаление заметки', color: COLORS.danger, icon: FileText },
    
    // Support
    ticket_create: { label: 'Создание тикета', color: '#3b82f6', icon: Mail },
    ticket_close: { label: 'Закрытие тикета', color: COLORS.success, icon: Check },
    ticket_reply: { label: 'Ответ на тикет', color: '#06b6d4', icon: Mail },
  };

  const getConfig = (action) => actionConfig[action] || { label: action, color: COLORS.textSecondary, icon: FileText };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDetails = (log) => {
    const details = log.details || {};
    const parts = [];
    
    // Client full info (enhanced)
    if (details.client_email) {
      parts.push({ label: '📧 Клиент', value: details.client_email, highlight: true });
    }
    if (details.client_name && details.client_name !== details.client_email) {
      parts.push({ label: '👤 Имя', value: details.client_name });
    }
    if (details.client_phone) {
      parts.push({ label: '📱 Телефон', value: details.client_phone });
    }
    if (details.client_status) {
      parts.push({ label: '📊 Статус', value: details.client_status });
    }
    if (details.client_tags && details.client_tags.length > 0) {
      parts.push({ label: '🏷️ Теги', value: details.client_tags.join(', ') });
    }
    
    // Fallback: Client ID if no email
    if (!details.client_email && log.client_id) {
      parts.push({ label: 'Клиент ID', value: log.client_id.slice(0, 12) + '...' });
    }
    
    // Amount
    if (details.amount !== undefined) {
      parts.push({ label: '💰 Сумма', value: `$${details.amount}` });
    }
    
    // Currency
    if (details.currency) {
      parts.push({ label: 'Валюта', value: details.currency });
    }
    
    // Status changes
    if (details.old_status && details.new_status) {
      parts.push({ label: '📊 Статус', value: `${details.old_status} → ${details.new_status}` });
    } else if (details.new_status) {
      parts.push({ label: '📊 Новый статус', value: details.new_status });
    }
    
    // Manager assignment
    if (details.manager_name) {
      parts.push({ label: '👤 Менеджер', value: details.manager_name });
    }
    
    // Tag operations
    if (details.tag) {
      parts.push({ label: '🏷️ Тег', value: details.tag });
    }
    
    // Count for mass operations
    if (details.client_count !== undefined) {
      parts.push({ label: '📋 Клиентов', value: details.client_count });
    }
    
    // Email info
    if (details.subject) {
      parts.push({ label: '📧 Тема', value: details.subject });
    }
    if (details.email) {
      parts.push({ label: '📨 Email', value: details.email });
    }
    
    // Team deletion info
    if (details.deleted_member_name) {
      parts.push({ label: '❌ Удалён', value: `${details.deleted_member_name} (${details.deleted_member_role})` });
    }
    if (details.clients_unassigned !== undefined && details.clients_unassigned > 0) {
      parts.push({ label: '⚠️ Откреплено клиентов', value: details.clients_unassigned });
    }
    
    // Deposit/transaction ID
    if (details.deposit_id) {
      parts.push({ label: 'Депозит', value: details.deposit_id.slice(0, 12) + '...' });
    }
    if (details.withdrawal_id) {
      parts.push({ label: 'Вывод', value: details.withdrawal_id.slice(0, 12) + '...' });
    }
    
    // Position info
    if (details.symbol) {
      parts.push({ label: '📈 Символ', value: details.symbol });
    }
    if (details.position_id) {
      parts.push({ label: 'Позиция', value: details.position_id.slice(0, 12) + '...' });
    }
    
    return parts;
  };

  const actionTypes = [
    { value: '', label: 'Все действия' },
    { group: 'Клиенты', items: [
      { value: 'client_create', label: 'Создание клиента' },
      { value: 'client_update', label: 'Обновление клиента' },
      { value: 'assign_manager', label: 'Назначение менеджера' },
      { value: 'status_change', label: 'Изменение статуса' },
      { value: 'tag_add', label: 'Добавление тега' },
      { value: 'tag_remove', label: 'Удаление тега' },
    ]},
    { group: 'Массовые операции', items: [
      { value: 'mass_assign', label: 'Массовое назначение' },
      { value: 'mass_status_change', label: 'Массовое изменение статуса' },
      { value: 'mass_tag', label: 'Массовое добавление тега' },
      { value: 'mass_email', label: 'Массовая рассылка' },
    ]},
    { group: 'Финансы', items: [
      { value: 'deposit_approve', label: 'Одобрение депозита' },
      { value: 'deposit_reject', label: 'Отклонение депозита' },
      { value: 'withdrawal_approve', label: 'Одобрение вывода' },
      { value: 'withdrawal_complete', label: 'Завершение вывода' },
      { value: 'balance_change', label: 'Изменение баланса' },
    ]},
    { group: 'Позиции', items: [
      { value: 'position_create', label: 'Создание позиции' },
      { value: 'position_update', label: 'Изменение позиции' },
      { value: 'position_close', label: 'Закрытие позиции' },
    ]},
    { group: 'Команда', items: [
      { value: 'team_create', label: 'Создание сотрудника' },
      { value: 'team_update', label: 'Изменение сотрудника' },
      { value: 'team_delete', label: 'Удаление сотрудника' },
      { value: 'permissions_update', label: 'Изменение прав' },
    ]},
    { group: 'Другое', items: [
      { value: 'email_sent', label: 'Email клиенту' },
      { value: 'admin_login', label: 'Вход в панель' },
      { value: 'note_add', label: 'Добавление заметки' },
    ]},
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            Аудит-лог
          </h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            История всех действий администраторов
          </p>
        </div>
        
        <button
          onClick={loadData}
          style={{
            padding: '10px 20px',
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <RefreshCw size={16} />
          Обновить
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('actions')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'actions' ? COLORS.primary : COLORS.card,
            border: `1px solid ${activeTab === 'actions' ? COLORS.primary : COLORS.border}`,
            borderRadius: '8px',
            color: activeTab === 'actions' ? '#000' : COLORS.textSecondary,
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <FileText size={16} />
          Действия ({logs.length})
        </button>
        <button
          onClick={() => setActiveTab('logins')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'logins' ? COLORS.primary : COLORS.card,
            border: `1px solid ${activeTab === 'logins' ? COLORS.primary : COLORS.border}`,
            borderRadius: '8px',
            color: activeTab === 'logins' ? '#000' : COLORS.textSecondary,
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <LogIn size={16} />
          Входы ({loginHistory.length})
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {activeTab === 'actions' && (
          <>
            {/* Action Type Filter */}
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              style={{
                padding: '10px 16px',
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                minWidth: '200px',
              }}
            >
              <option value="">📋 Все действия</option>
              {actionTypes.filter(t => t.group).map(group => (
                <optgroup key={group.group} label={group.group}>
                  {group.items.map(item => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            
            {/* Admin Filter */}
            <select
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              style={{
                padding: '10px 16px',
                backgroundColor: COLORS.card,
                border: `1px solid ${adminFilter ? COLORS.primary : COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                minWidth: '200px',
              }}
            >
              <option value="">👤 Все сотрудники</option>
              {adminList.map(admin => (
                <option key={admin.id} value={admin.id}>
                  {admin.name || admin.email} ({admin.role === 'super_admin' ? 'Супер Админ' : admin.role === 'admin' ? 'Админ' : admin.role === 'manager' ? 'Менеджер' : 'Поддержка'})
                </option>
              ))}
            </select>
          </>
        )}
        
        {/* Days Filter */}
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          style={{
            padding: '10px 16px',
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px',
          }}
        >
          <option value={7}>Последние 7 дней</option>
          <option value={14}>Последние 14 дней</option>
          <option value={30}>Последние 30 дней</option>
          <option value={90}>Последние 90 дней</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textSecondary }}>
          Загрузка...
        </div>
      ) : activeTab === 'actions' ? (
        <div style={{
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
        }}>
          {logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textSecondary }}>
              Нет записей в аудит-логе
            </div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
              {logs.map((log, i) => {
                const config = getConfig(log.action_type);
                const Icon = config.icon;
                const details = log.details || {};
                
                // Extract key information
                const adminName = log.admin_name || log.admin_email || 'Неизвестный';
                const clientEmail = details.client_email || details.email;
                const clientName = details.client_name;
                const clientInfo = clientEmail || clientName || (log.client_id ? `ID: ${log.client_id.slice(0, 12)}` : null);
                
                return (
                  <div
                    key={log.id || i}
                    style={{
                      padding: '16px 20px',
                      borderBottom: i < logs.length - 1 ? `1px solid ${COLORS.border}20` : 'none',
                    }}
                  >
                    {/* Header Row: Action + Time */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: `${config.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Icon size={18} color={config.color} />
                        </div>
                        <div>
                          <span style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600' }}>
                            {config.label}
                          </span>
                          <span style={{ 
                            marginLeft: '10px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: `${config.color}15`,
                            color: config.color,
                            fontSize: '10px',
                            fontWeight: '600',
                          }}>
                            {log.action_type}
                          </span>
                        </div>
                      </div>
                      <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        {formatDate(log.created_at)}
                      </span>
                    </div>

                    {/* Main Info: WHO → CLIENT → WHAT */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: clientInfo ? '1fr auto 1fr auto 1fr' : '1fr auto 2fr',
                      gap: '12px',
                      alignItems: 'center',
                      padding: '14px 16px',
                      backgroundColor: COLORS.background,
                      borderRadius: '10px',
                      marginBottom: '10px',
                    }}>
                      {/* WHO */}
                      <div>
                        <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase' }}>
                          👤 Кто совершил
                        </div>
                        <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                          {adminName}
                        </div>
                      </div>
                      
                      {clientInfo && (
                        <>
                          <div style={{ color: COLORS.border, fontSize: '20px' }}>→</div>
                          
                          {/* CLIENT */}
                          <div>
                            <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase' }}>
                              🎯 Над клиентом
                            </div>
                            <div style={{ color: COLORS.primary, fontSize: '14px', fontWeight: '600' }}>
                              {clientInfo}
                            </div>
                            {clientName && clientName !== clientEmail && (
                              <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>{clientName}</div>
                            )}
                          </div>
                        </>
                      )}
                      
                      <div style={{ color: COLORS.border, fontSize: '20px' }}>→</div>
                      
                      {/* WHAT */}
                      <div>
                        <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase' }}>
                          📋 Действие
                        </div>
                        <div style={{ color: config.color, fontSize: '14px', fontWeight: '600' }}>
                          {config.label}
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    {Object.keys(details).filter(k => !['client_email', 'client_name', 'client_id'].includes(k)).length > 0 && (
                      <div style={{
                        padding: '12px 14px',
                        backgroundColor: `${config.color}08`,
                        borderRadius: '8px',
                        border: `1px solid ${config.color}20`,
                      }}>
                        <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '8px', textTransform: 'uppercase' }}>
                          📝 Детали действия
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          {/* Amount */}
                          {details.amount !== undefined && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>💰 Сумма: </span>
                              <span style={{ color: COLORS.success, fontWeight: '600' }}>${details.amount}</span>
                            </div>
                          )}
                          
                          {/* Status Change */}
                          {details.old_status && details.new_status && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>📊 Статус: </span>
                              <span style={{ color: COLORS.danger }}>{details.old_status}</span>
                              <span style={{ color: COLORS.textSecondary }}> → </span>
                              <span style={{ color: COLORS.success }}>{details.new_status}</span>
                            </div>
                          )}
                          {details.new_status && !details.old_status && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>📊 Статус: </span>
                              <span style={{ color: COLORS.success, fontWeight: '600' }}>{details.new_status}</span>
                            </div>
                          )}
                          
                          {/* Manager */}
                          {details.manager_name && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>👤 Менеджер: </span>
                              <span style={{ color: COLORS.text, fontWeight: '600' }}>{details.manager_name}</span>
                            </div>
                          )}
                          
                          {/* Tag */}
                          {details.tag && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>🏷️ Тег: </span>
                              <span style={{ color: COLORS.primary, fontWeight: '600' }}>{details.tag}</span>
                            </div>
                          )}
                          
                          {/* New tag for mass assign */}
                          {details.new_tag && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>🏷️ Новый тег: </span>
                              <span style={{ color: COLORS.primary, fontWeight: '600' }}>{details.new_tag}</span>
                            </div>
                          )}
                          
                          {/* Client Count for mass operations */}
                          {details.client_count !== undefined && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>👥 Клиентов: </span>
                              <span style={{ color: COLORS.text, fontWeight: '600' }}>{details.client_count}</span>
                            </div>
                          )}
                          
                          {/* Affected Clients List for mass operations */}
                          {details.affected_clients && details.affected_clients.length > 0 && (
                            <div style={{ width: '100%', marginTop: '8px' }}>
                              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '6px', textTransform: 'uppercase' }}>
                                📋 Затронутые клиенты:
                              </div>
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '6px',
                                maxHeight: details.affected_clients.length > 10 ? '120px' : 'auto',
                                overflowY: details.affected_clients.length > 10 ? 'auto' : 'visible',
                                padding: '8px',
                                backgroundColor: COLORS.background,
                                borderRadius: '6px',
                              }}>
                                {details.affected_clients.map((client, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      backgroundColor: `${COLORS.primary}15`,
                                      border: `1px solid ${COLORS.primary}30`,
                                      color: COLORS.text,
                                      fontSize: '11px',
                                    }}
                                    title={client.name || client.email}
                                  >
                                    {client.email || client.name || client.id?.slice(0, 8)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Email Subject */}
                          {details.subject && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>📧 Тема: </span>
                              <span style={{ color: COLORS.text, fontWeight: '500' }}>{details.subject}</span>
                            </div>
                          )}
                          
                          {/* Symbol for trades */}
                          {details.symbol && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>📈 Пара: </span>
                              <span style={{ color: COLORS.primary, fontWeight: '600' }}>{details.symbol}</span>
                            </div>
                          )}
                          
                          {/* Team Delete Info */}
                          {details.deleted_member_name && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>❌ Удалён: </span>
                              <span style={{ color: COLORS.danger, fontWeight: '600' }}>{details.deleted_member_name}</span>
                            </div>
                          )}
                          {details.clients_unassigned > 0 && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>⚠️ Откреплено: </span>
                              <span style={{ color: COLORS.danger, fontWeight: '600' }}>{details.clients_unassigned} клиентов</span>
                            </div>
                          )}
                          
                          {/* Client Status & Tags (for context) */}
                          {details.client_status && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>📊 Статус клиента: </span>
                              <span style={{ color: COLORS.text, fontWeight: '500' }}>{details.client_status}</span>
                            </div>
                          )}
                          {details.client_tags && details.client_tags.length > 0 && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>🏷️ Теги клиента: </span>
                              <span style={{ color: COLORS.text, fontWeight: '500' }}>{details.client_tags.join(', ')}</span>
                            </div>
                          )}
                          
                          {/* Phone */}
                          {details.client_phone && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>📱 Телефон: </span>
                              <span style={{ color: COLORS.text, fontWeight: '500' }}>{details.client_phone}</span>
                            </div>
                          )}
                          
                          {/* Currency */}
                          {details.currency && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: COLORS.textSecondary }}>💱 Валюта: </span>
                              <span style={{ color: COLORS.text, fontWeight: '500' }}>{details.currency}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Login History Tab */
        <div style={{
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
            padding: '14px 20px',
            backgroundColor: COLORS.background,
            borderBottom: `1px solid ${COLORS.border}`,
            gap: '12px',
          }}>
            <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>СОТРУДНИК</span>
            <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>РОЛЬ</span>
            <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>IP АДРЕС</span>
            <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ДАТА/ВРЕМЯ</span>
          </div>
          
          {loginHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textSecondary }}>
              Нет записей о входах
            </div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
              {loginHistory.map((login, i) => (
                <div
                  key={login.id || i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    padding: '14px 20px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    gap: '12px',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: '#3b82f620',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <User size={16} color="#3b82f6" />
                    </div>
                    <div>
                      <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
                        {login.admin_name || login.admin_email || 'Unknown'}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                        {login.admin_email}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      backgroundColor: login.role === 'super_admin' ? '#f43f5e20' : 
                                      login.role === 'admin' ? '#8b5cf620' : '#3b82f620',
                      color: login.role === 'super_admin' ? '#f43f5e' : 
                             login.role === 'admin' ? '#8b5cf6' : '#3b82f6',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>
                      {login.role === 'super_admin' ? 'Супер Админ' : 
                       login.role === 'admin' ? 'Админ' : 
                       login.role === 'manager' ? 'Менеджер' : login.role}
                    </span>
                  </div>
                  
                  <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                    {login.ip_address || 'N/A'}
                  </div>
                  
                  <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                    {formatDate(login.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAuditLog;
